import { combineReducers } from 'redux';
import extend from 'lodash/extend';
import find from 'lodash/find';

import {
  queryJourneysForRides,
  getEventsGeoData,
  checkJourneysEverExist,
  checkJourneysForMonthExist, appealEventPenalty,
} from '../sagas/rides/actions';
import { changeCurrentDevice } from '../components/Header/actions';
import {
  resetCurrentJourney,
  changeCurrentJourney,
  previewJourney,
  resetPreviewedJourney,
} from '../components/Sidebar/RidesSidebar/actions';
import { logoutButtonClick } from '../components/Auth/actions';
import { appealEvent, cancelEventAppeal } from '../components/Rides/actions';

const areJourneysEverLoaded = (state = false, action) => {
  if (action.type === checkJourneysEverExist.done.type || action.type === checkJourneysEverExist.failed.type) {
    return true;
  }
  if (action.type === checkJourneysEverExist.started.type) {
    return false;
  }
  if (action.type === logoutButtonClick.type) {
    return false;
  }
  return state;
};

const areJourneysForMonthLoaded = (state = false, action) => {
  if (action.type === checkJourneysForMonthExist.done.type || action.type === checkJourneysForMonthExist.failed.type) {
    return true;
  }
  if (action.type === checkJourneysForMonthExist.started.type) {
    return false;
  }
  if (action.type === logoutButtonClick.type) {
    return false;
  }
  return state;
};

const areJourneysEverExist = (state = false, action) => {
  if (action.type === checkJourneysEverExist.done.type) {
    return action.payload.pagingInfo.totalCount > 0;
  }
  if (action.type === checkJourneysEverExist.failed.type) {
    return false;
  }
  return state;
};

const areJourneysForMonthExist = (state = false, action) => {
  if (action.type === checkJourneysForMonthExist.done.type) {
    return action.payload.pagingInfo.totalCount > 0;
  }
  if (action.type === checkJourneysForMonthExist.failed.type) {
    return false;
  }
  return state;
};

const journeys = (state = [], action) => {
  if (action.type === queryJourneysForRides.done.type) {
    return action.payload.result.cursorInfo.currentOffset >= state.length + action.payload.result.data.length
      ? [...state, ...action.payload.result.data]
      : action.payload.result.data;
  }

  if (action.type === changeCurrentDevice.type) {
    return [];
  }

  if (action.type === logoutButtonClick.type) {
    return [];
  }

  return state;
};

const journeysLoaded = (state = false, action) => {
  if (action.type === queryJourneysForRides.done.type) {
    return true;
  }
  if (action.type === changeCurrentDevice.type) {
    return false;
  }

  if (action.type === logoutButtonClick.type) {
    return false;
  }
  return state;
};

const filter = (state = {}, action) => {
  if (action.type === queryJourneysForRides.done.type) {
    return action.payload.params;
  }

  if (action.type === changeCurrentDevice.type) {
    return {};
  }

  if (action.type === logoutButtonClick.type) {
    return {};
  }

  return state;
};

const totalCount = (state = 0, action) => {
  if (action.type === queryJourneysForRides.done.type) {
    return action.payload.result.pagingInfo.totalCount;
  }

  if (action.type === changeCurrentDevice.type) {
    return 0;
  }

  if (action.type === logoutButtonClick.type) {
    return 0;
  }

  return state;
};

const hasMore = (state = false, action) => {
  if (action.type === queryJourneysForRides.done.type) {
    return action.payload.result.cursorInfo.itemsLeft > 0;
  }

  if (action.type === logoutButtonClick.type) {
    return false;
  }

  return state;
};

const currentJourney = (state = null, action) => {
  if (action.type === resetCurrentJourney.type) {
    return null;
  }

  if (action.type === changeCurrentJourney.type) {
    return action.payload.journey;
  }

  if (action.type === getEventsGeoData.done.type) {
    const journey = { ...state };
    if (journey && journey.journey) {
      const events = journey.journey.events.filter(e => e.penalties).map((e, index) => {
        e.geoData = action.payload.result[index];
        return e;
      });

      journey.journey.events = journey.journey.events.map(e => extend(e, find(events, item => item.id === e.id)));

      return { ...journey };
    }
    return null;
  }

  if (action.type === logoutButtonClick.type) {
    return null;
  }

  return state;
};

const currentPreviewedJourney = (state = null, action) => {
  if (action.type === previewJourney.type) {
    return action.payload.journey;
  }

  if (action.type === resetPreviewedJourney.type) {
    return null;
  }

  if (action.type === getEventsGeoData.done.type) {
    const journey = { ...state };
    const events = journey.journey.events.filter(e => e.penalties).map((e, index) => {
      e.geoData = action.payload.result[index];
      return e;
    });

    journey.journey.events = journey.journey.events.map(e => extend(e, find(events, item => item.id === e.id)));

    return { ...journey };
  }

  return state;
};

const isPreviewing = (state = false, action) => {
  if (action.type === previewJourney.type) {
    return true;
  }

  if (action.type === changeCurrentJourney.type) {
    return false;
  }

  if (action.type === resetPreviewedJourney.type) {
    return false;
  }

  return state;
};

const appealing = (state = false, action) => {
  if (action.type === appealEvent.type) {
    return true;
  }
  if (action.type === cancelEventAppeal.type) {
    return false;
  }
  return state;
};

const appealingEvent = (state = {}, action) => {
  if (action.type === appealEvent.type) {
    return action.payload.event;
  }
  if (action.type === cancelEventAppeal.type) {
    return {};
  }
  return state;
};

const appealingStatus = (state = 'Unknown', action) => {
  if (action.type === appealEventPenalty.started.type) {
    return 'Started';
  }
  if (action.type === appealEventPenalty.done.type) {
    return 'Done';
  }
  if (action.type === appealEventPenalty.failed.type) {
    return 'Failed';
  }

  if (action.type === cancelEventAppeal.type) {
    return 'Unknown';
  }

  if (action.type === logoutButtonClick.type) {
    return 'Unknown';
  }
  return state;
};


export default combineReducers({
  areJourneysEverLoaded,
  areJourneysForMonthLoaded,
  areJourneysEverExist,
  areJourneysForMonthExist,
  journeys,
  totalCount,
  hasMore,
  filter,
  currentJourney,
  currentPreviewedJourney,
  isPreviewing,
  journeysLoaded,
  appealing,
  appealingEvent,
  appealingStatus,
});



// WEBPACK FOOTER //
// ./src/reducers/rides.js